/**
 * TaxonomiesPanel.js — Phase H
 * Tech Specialist UI to manage WordPress post Categories and Tags.
 * Two tabs in one panel since they share the same shape.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TaxonomiesPanel({ orgId }) {
  const [taxonomy, setTaxonomy] = useState('categories');

  return (
    <div data-testid="taxonomies-panel" className="p-6 space-y-4">
      <LiveSiteHeader orgId={orgId} surfaceLabel={taxonomy === 'categories' ? 'Categories' : 'Tags'}
                       wpPath={taxonomy === 'categories' ? '/category' : '/tag'} />
      <div>
        <h2 className="text-lg font-semibold">Post Taxonomies</h2>
        <p className="text-sm text-gray-500">
          Manage WordPress post categories and tags used by the News
          (Posts) tab — without ever opening wp-admin.
        </p>
      </div>
      <div className="flex gap-2 border-b">
        {['categories', 'tags'].map((t) => (
          <button
            key={t}
            data-testid={`taxonomies-tab-${t}`}
            onClick={() => setTaxonomy(t)}
            className={`px-4 py-2 text-sm border-b-2 ${
              taxonomy === t ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-500'
            }`}
          >{t === 'categories' ? 'Categories' : 'Tags'}</button>
        ))}
      </div>
      <TermList orgId={orgId} taxonomy={taxonomy} />
    </div>
  );
}

function TermList({ orgId, taxonomy }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bridgeMissing, setBridgeMissing] = useState(false);
  const [wpDown, setWpDown] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTerm, setNewTerm] = useState({ name: '', slug: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setBridgeMissing(false); setWpDown(false);
    try {
      const { data } = await axios.get(`${API}/wp-taxonomies/${orgId}/${taxonomy}`, { withCredentials: true });
      // backend unwraps Envelope → either {items: [...]} or {<name>: [...]}
      const list = Array.isArray(data) ? data
                   : data.items || data.categories || data.tags || [];
      setItems(list);
    } catch (e) {
      const d = e.response?.data?.detail || '';
      if (e.response?.status === 503) setWpDown(true);
      else if (e.response?.status === 502 && /bridge_lacks/.test(d)) setBridgeMissing(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, taxonomy]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newTerm.name.trim()) return;
    setCreating(true);
    try {
      await axios.post(
        `${API}/wp-taxonomies/${orgId}/${taxonomy}`,
        { ...newTerm, name: newTerm.name.trim() },
        { withCredentials: true },
      );
      setNewTerm({ name: '', slug: '', description: '' });
      await load();
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEdit({ name: it.name || '', slug: it.slug || '', description: it.description || '' });
  };
  const saveEdit = async () => {
    await axios.patch(
      `${API}/wp-taxonomies/${orgId}/${taxonomy}/${editingId}`,
      edit,
      { withCredentials: true },
    );
    setEditingId(null);
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm(`Delete this ${taxonomy.slice(0,-1)}? Posts using it will lose the assignment.`)) return;
    await axios.delete(`${API}/wp-taxonomies/${orgId}/${taxonomy}/${id}`, { withCredentials: true });
    await load();
  };

  if (wpDown) return <Banner tone="red" title="WordPress unreachable" body="Re-toggle the Live Link tunnel." onRetry={load} />;
  if (bridgeMissing) return <Banner tone="amber" title="Bridge plugin update needed" body={`The bridge plugin doesn't expose /${taxonomy}. Ship docs/wp-bridge-extensions/TAXONOMIES.md.`} onRetry={load} />;

  return (
    <div>
      {/* Create */}
      <div className="border rounded p-3 mb-4 grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3">
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input data-testid="tax-new-name" value={newTerm.name} onChange={(e) => setNewTerm({ ...newTerm, name: e.target.value })} placeholder={taxonomy === 'categories' ? 'e.g. Game Recaps' : 'e.g. championship'} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div className="col-span-3">
          <label className="block text-xs text-gray-500 mb-1">Slug (optional)</label>
          <input data-testid="tax-new-slug" value={newTerm.slug} onChange={(e) => setNewTerm({ ...newTerm, slug: e.target.value })} placeholder="auto" className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div className="col-span-5">
          <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
          <input data-testid="tax-new-desc" value={newTerm.description} onChange={(e) => setNewTerm({ ...newTerm, description: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <button data-testid="tax-create" onClick={create} disabled={creating || !newTerm.name.trim()} className="col-span-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">+ Add</button>
      </div>

      {loading && <div className="text-gray-500 text-sm">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-gray-500 text-sm py-4" data-testid="tax-empty">
          No {taxonomy} yet. Create one above — it&apos;ll be available immediately on WP.
        </div>
      )}

      <ul className="divide-y border rounded" data-testid={`tax-list-${taxonomy}`}>
        {items.map((it) => (
          <li key={it.id} className="px-3 py-2" data-testid={`tax-row-${it.id}`}>
            {editingId === it.id ? (
              <div className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`tax-edit-name-${it.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="col-span-3 border rounded px-2 py-1 text-sm" />
                <input data-testid={`tax-edit-slug-${it.id}`} value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className="col-span-3 border rounded px-2 py-1 text-sm" />
                <input data-testid={`tax-edit-desc-${it.id}`} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="col-span-4 border rounded px-2 py-1 text-sm" />
                <button data-testid={`tax-save-${it.id}`} onClick={saveEdit} className="col-span-1 px-2 py-1 bg-blue-600 text-white text-xs rounded">Save</button>
                <button data-testid={`tax-cancel-${it.id}`} onClick={() => setEditingId(null)} className="col-span-1 px-2 py-1 border text-xs rounded">×</button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{it.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    slug: {it.slug} · {it.count || 0} post{(it.count || 0) === 1 ? '' : 's'}
                    {it.description && ` · ${it.description}`}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`tax-edit-btn-${it.id}`} onClick={() => startEdit(it)} className="px-2 py-1 text-xs border rounded hover:bg-gray-50">Edit</button>
                  <button data-testid={`tax-delete-${it.id}`} onClick={() => remove(it.id)} className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">×</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Banner({ tone, title, body, onRetry }) {
  const cls = tone === 'red' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900';
  const btn = tone === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700';
  return (
    <div className={`p-4 rounded border ${cls}`}>
      <strong>{title}</strong>
      <p className="mt-1 text-sm">{body}</p>
      <button onClick={onRetry} className={`mt-2 px-3 py-1 text-white rounded text-xs ${btn}`}>Retry</button>
    </div>
  );
}
